import React, { useEffect, useMemo, useState } from "react";
import MasterLayout from "../masterLayout/MasterLayout";
import Breadcrumb from "../components/Breadcrumb";
import { Modal } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "..";
import { useNavigate, useParams } from "react-router-dom";

const AccountLogsPage = () => {
  const { id } = useParams(); // accountId
  const navigate = useNavigate();
  const locationId = localStorage.getItem("locationID");

  const [account, setAccount] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableError, setTableError] = useState("");

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedLogId, setSelectedLogId] = useState(null);

  const [form, setForm] = useState({
    amount: "",
    reference_title: "",
  });

  const accountLabel = useMemo(() => {
    if (!account) return "";
    if (account.type === "bank") {
      return `${account.bankAccountTitle || ""} - ${account.bankAccountNo || ""}`.trim();
    }
    return `${account.accountTitle || ""} - ${account.accountNo || ""}`.trim();
  }, [account]);

  const totalIn = useMemo(() => {
    return logs.reduce((sum, l) => sum + (l.direction === "IN" ? Number(l.amount || 0) : 0), 0);
  }, [logs]);

  const resetForm = () => {
    setForm({ amount: "", reference_title: "" });
    setIsEditing(false);
    setSelectedLogId(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (log) => {
    setIsEditing(true);
    setSelectedLogId(log.id);
    setForm({
      amount: log.amount ?? "",
      reference_title: log.reference_title ?? "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    resetForm();
    setShowModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  // ✅ Fetch account (your response is { accounts: {...} })
  const fetchAccount = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accounts/${id}`, {
        headers: { Accept: "*/*" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch account");
      setAccount(data.accounts || null);
    } catch (err) {
      console.error("fetchAccount:", err);
    }
  };

  // ✅ Fetch logs (your response is { data: [...] })
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounts/accounts/logs/${id}`, {
        headers: { Accept: "*/*" },
      });
      const json = await res.json();

      if (!res.ok) throw new Error(json.message || "Failed to fetch logs");

      const list = Array.isArray(json?.data) ? json.data : [];
      setLogs(list);
      setTableError("");
    } catch (err) {
      console.error("fetchLogs:", err);
      setTableError(err.message || "Unable to load logs");
      setLogs([]);
      toast.error(err.message || "Unable to load logs", { autoClose: 2500 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccount();
    fetchLogs();
    // eslint-disable-next-line
  }, [id]);

  // ✅ Add daily income (IN only)
  const submitAddIncome = async (e) => {
    e.preventDefault();

    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounts/accounts/daily-income`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify({
          accountId: Number(id),
          amount: form.amount,
          title: form.reference_title || "Daily Income (Other Source)",
          locationId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to add daily income");

      toast.success("Income added successfully", { autoClose: 2000 });
      closeModal();
      fetchLogs();
      fetchAccount();
    } catch (err) {
      console.error("submitAddIncome:", err);
      toast.error(err.message || "Failed to add income", { autoClose: 2500 });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Edit log (recommended only for daily_income)
  const submitEditLog = async (e) => {
    e.preventDefault();

    if (!selectedLogId) return toast.error("Invalid log selected");
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/account-logs/${selectedLogId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "*/*",
        },
        body: JSON.stringify({
          amount: form.amount,
          reference_title: form.reference_title,
          locationId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to update log");

      toast.success("Log updated", { autoClose: 2000 });
      closeModal();
      fetchLogs();
      fetchAccount();
    } catch (err) {
      console.error("submitEditLog:", err);
      toast.error(err.message || "Failed to update log", { autoClose: 2500 });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete log (recommended only for daily_income)
  const deleteLog = async (log) => {
    // if (!window.confirm("Delete this log?")) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/accounts/accounts/logs/${log.id}`, {
        method: "DELETE",
        headers: { Accept: "*/*" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to delete log");

      toast.success("Log deleted", { autoClose: 1000 });
      fetchLogs();
      fetchAccount();
    } catch (err) {
      console.error("deleteLog:", err);
      toast.error(err.message || "Failed to delete log", { autoClose: 2500 });
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (v) => Number(v || 0).toLocaleString();

  const refBadgeClass = (ref) => {
    if (ref === "daily_income") return "bg-success-subtle text-success border border-success-subtle";
    if (ref === "invoice_payment") return "bg-primary-subtle text-primary border border-primary-subtle";
    return "bg-light text-dark border";
  };

  return (
    <MasterLayout>
      <Breadcrumb title="Account Logs" />

      {/* Header Card */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <button
              className="btn btn-light border"
              onClick={() => navigate(-1)}
              title="Back"
            >
              <i className="ph ph-arrow-left"></i>
            </button>

            <div>
              <div className="fw-bold" style={{ fontSize: 18 }}>
                {accountLabel || `Account #${id}`}
              </div>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Type: <span className="fw-semibold text-uppercase">{account?.type || "—"}</span>
                {account?.locationId ? <span className="ms-2">• Location: {account.locationId}</span> : null}
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <div className="px-3 py-2 border rounded" style={{ background: "#f8fafc", minWidth: 150 }}>
              <div className="text-muted" style={{ fontSize: 12 }}>Current Balance</div>
              <div className="fw-bold" style={{ fontSize: 16 }}>
                {formatMoney(account?.balance)}
              </div>
            </div>

            <div className="px-3 py-2 border rounded" style={{ background: "#f8fafc", minWidth: 150 }}>
              <div className="text-muted" style={{ fontSize: 12 }}>Total IN</div>
              <div className="fw-bold" style={{ fontSize: 16 }}>
                {formatMoney(totalIn)}
              </div>
            </div>

            <div className="px-3 py-2 border rounded" style={{ background: "#f8fafc", minWidth: 120 }}>
              <div className="text-muted" style={{ fontSize: 12 }}>Logs</div>
              <div className="fw-bold" style={{ fontSize: 16 }}>
                {logs.length}
              </div>
            </div>

            <button
              className="btn btn-success"
              onClick={openAddModal}
              disabled={loading}
              title="Add Daily Income"
            >
              <i className="ph ph-plus-circle me-1"></i>
              Add Income
            </button>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className="card basic-data-table">
        <div className="card-header d-flex align-items-center justify-content-between">
          <h5 className="card-title mb-0">Logs</h5>

          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={fetchLogs}
            disabled={loading}
          >
            <i className="ph ph-arrows-clockwise me-1"></i>
            Refresh
          </button>
        </div>

        <div className="card-body">
          {tableError && <div className="alert alert-danger">{tableError}</div>}

          <div className="table-responsive">
            <table className="table bordered-table mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ minWidth: 170 }}>Date</th>
                  <th style={{ minWidth: 90 }}>Direction</th>
                  <th>Title</th>
                  <th style={{ minWidth: 140 }}>Reference</th>
                  <th className="text-end" style={{ minWidth: 120 }}>Amount</th>
                  {/* <th className="text-end" style={{ minWidth: 140 }}>Balance After</th> */}
                  <th className="text-end" style={{ minWidth: 130 }}>Action</th>
                </tr>
              </thead>

              <tbody>
                {logs.map((log) => {
                  const canEdit = log.reference_type === "daily_income"; // recommended
                  return (
                    <tr key={log.id}>
                      <td>
                        {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          #{log.id}
                        </div>
                      </td>

                      <td>
                        <span className={`badge ${log.direction === "IN" ? "bg-success" : "bg-danger"}`}>
                          {log.direction}
                        </span>
                      </td>

                      <td className="fw-semibold">
                        {log.reference_title || "—"}
                        {log.reference_id ? (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            Ref ID: {log.reference_id}
                          </div>
                        ) : null}
                      </td>

                      <td>
                        <span className={`badge ${refBadgeClass(log.reference_type)}`}>
                          {log.reference_type || "—"}
                        </span>
                      </td>

                      <td className="text-end fw-bold">
                        {formatMoney(log.amount)}
                      </td>

                      {/* <td className="text-end">
                        {log.balance_after !== undefined ? formatMoney(log.balance_after) : "—"}
                      </td> */}

                      <td className="text-end text-nowrap">
                        {canEdit && (
                          <>
                           {/* <button
                          className={`btn btn-light border me-2 ${!canEdit ? "disabled" : ""}`}
                          onClick={() => canEdit && openEditModal(log)}
                          disabled={loading || !canEdit}
                          title={canEdit ? "Edit Log" : "Only Daily Income logs are editable"}
                        >
                          <i className="ph ph-pencil-simple"></i>
                        </button> */}

                        <button
                          className={`btn btn-outline-danger ${!canEdit ? "disabled" : ""}`}
                          onClick={() => deleteLog(log)}
                        //   disabled={loading || !canEdit}
                          title={canEdit ? "Delete Log" : "Only Daily Income logs are deletable"}
                        >
                        <i class="ri-delete-bin-line"></i>
                        </button>
                          
                          </>
                        )}
                       
                      </td>
                    </tr>
                  );
                })}

                {logs.length === 0 && !tableError && (
                  <tr>
                    <td colSpan="7" className="text-center text-muted py-4">
                      No logs found for this account.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Edit Daily Income" : "Add Daily Income"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="mb-3 p-2 border rounded" style={{ background: "#f8fafc" }}>
            <div className="fw-bold" style={{ fontSize: 13 }}>Account</div>
            <div className="text-muted" style={{ fontSize: 13 }}>{accountLabel || `#${id}`}</div>
          </div>

          <form onSubmit={isEditing ? submitEditLog : submitAddIncome}>
            <div className="mb-3">
              <label className="form-label fw-bold">Amount</label>
              <input
                type="number"
                name="amount"
                value={form.amount}
                onChange={handleChange}
                className="form-control"
                min="1"
                placeholder="Enter amount"
                required
              />
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Title / Details</label>
              <input
                type="text"
                name="reference_title"
                value={form.reference_title}
                onChange={handleChange}
                className="form-control"
                placeholder="e.g. Daily income from other source"
              />
            </div>

            <div className="d-flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                style={{ background: "#144BD6", borderColor: "#144BD6" }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ph ph-check-circle me-1"></i>
                    {isEditing ? "Update" : "Save"}
                  </>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModal}
                disabled={loading}
              >
                <i className="ph ph-x-circle me-1"></i>
                Cancel
              </button>
            </div>
          </form>

          {!isEditing && (
            <div className="alert alert-info mt-3 mb-0" style={{ fontSize: 13 }}>
              This will be saved as <b>IN</b> (daily income) and balance will update automatically.
            </div>
          )}
        </Modal.Body>
      </Modal>

      <ToastContainer />
    </MasterLayout>
  );
};

export default AccountLogsPage;
